"use client";

/**
 * Tabs — 콘텐츠 전환용 탭 (Radix Tabs 기반)
 *
 * AdminTabs 의 기반 프리미티브. 직접 조합해서 쓸 수도 있다.
 * variant 로 스타일을 고른다 (기본 "line").
 * - "line": 밑줄 탭 (antd 기본 스타일). 배경색이 아니라 밑줄/글자색으로 활성 상태를 표시해서
 *   무채색(그레이스케일) 테마처럼 배경 대비가 약한 경우에도 항상 잘 보인다.
 * - "segment": 알약형 배경 탭 (shadcn 기본 스타일). 배경색 대비가 뚜렷한 테마에서 사용.
 *
 * @example
 * <Tabs defaultValue="info">
 *   <TabsList>
 *     <TabsTrigger value="info">기본 정보</TabsTrigger>
 *     <TabsTrigger value="history">변경 이력</TabsTrigger>
 *   </TabsList>
 *   <TabsContent value="info">...</TabsContent>
 *   <TabsContent value="history">...</TabsContent>
 * </Tabs>
 * <Tabs defaultValue="info" variant="segment">...</Tabs>
 */
import { type ComponentProps, createContext, useContext } from "react";
import * as TabsPrimitive from "@radix-ui/react-tabs";

import { cn } from "../../lib/utils";

export type TabsVariant = "line" | "segment";

const TabsVariantContext = createContext<TabsVariant>("line");

function Tabs({
  className,
  variant = "line",
  ...props
}: ComponentProps<typeof TabsPrimitive.Root> & { variant?: TabsVariant }) {
  return (
    <TabsVariantContext.Provider value={variant}>
      <TabsPrimitive.Root
        data-slot="tabs"
        className={cn("flex flex-col gap-2", className)}
        {...props}
      />
    </TabsVariantContext.Provider>
  );
}

function TabsList({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.List>) {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.List
      data-slot="tabs-list"
      className={cn(
        "inline-flex w-fit items-center",
        variant === "segment"
          ? "h-9 justify-center rounded-lg bg-muted p-[3px] text-muted-foreground"
          : "gap-4",
        className
      )}
      {...props}
    />
  );
}

function TabsTrigger({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Trigger>) {
  const variant = useContext(TabsVariantContext);
  return (
    <TabsPrimitive.Trigger
      data-slot="tabs-trigger"
      className={cn(
        "inline-flex items-center justify-center gap-1.5 text-sm font-medium whitespace-nowrap transition-[color,box-shadow,background-color] outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50",
        variant === "segment"
          ? "h-[calc(100%-1px)] flex-1 rounded-md border border-transparent px-2 py-1 text-muted-foreground focus-visible:border-ring data-[state=active]:bg-primary data-[state=active]:text-primary-foreground data-[state=active]:shadow-sm"
          : "-mb-px rounded-t-md border-b-2 border-transparent px-2 pb-2 text-muted-foreground hover:bg-muted/50 hover:text-foreground data-[state=active]:border-primary data-[state=active]:text-primary",
        className
      )}
      {...props}
    />
  );
}

function TabsContent({
  className,
  ...props
}: ComponentProps<typeof TabsPrimitive.Content>) {
  return (
    <TabsPrimitive.Content
      data-slot="tabs-content"
      className={cn("flex-1 outline-none", className)}
      {...props}
    />
  );
}

export { Tabs, TabsList, TabsTrigger, TabsContent };
